<template>
  <div class="infoCard full-width">
    <q-card class="cardBox q-mx-lg border-radius-10 bg-white">
      <q-card-section class="q-py-sm text-center">
        <q-img
          basic
          :placeholder-src="$ossLimit(holderLogo, 200)"
          :src="$ossLimit(storeInfo.logo || holderLogo, 200)"
          class="logoImg"
        />
        <h1 class="font-size-18 text-weight-bold q-my-none storeName">
          {{ storeInfo.name }}
        </h1>
        <template v-if="openLocation">
          <span v-if="distance <= 40" class="distance">
            {{ $t('home.distance', { distance: distance || 0 }) }}
          </span>
          <span v-else class="distance">{{ $t('home.exceed') }}</span>
        </template>
        <template v-else>
          <span class="distance">{{ $t('home.location') }}</span>
        </template>
        <span>
          <slot name="nearest" />
        </span>
      </q-card-section>
      <q-card-section class="q-py-sm row no-wrap justify-start items-center">
        <q-icon
          size="20px"
          class="beforeIcon vertical-middle"
          name="font:dm-location"
        />
        <span class="ellipsis">
          {{ place.address3 || $t('common.undifined') }}
        </span>
        <a
          v-if="place.address3"
          class="q-ml-auto text-no-wrap linkText q-pl-md"
          href="javascript:void(0);"
          @click="handleShowMap"
        >
          {{ $t('home.seeMap') }}
        </a>
      </q-card-section>
      <q-card-section class="q-py-sm expansionBox">
        <q-expansion-item class="q-mx-none">
          <template #header>
            <q-icon
              size="18px"
              class="beforeIcon time"
              name="font:dm-timeout"
            />
            <span>
              <span v-if="showTime" class="status statusOpen">
                {{ $t('home.currently') }}
                <b class="open">{{ $t('home.onlyOpen') }}</b>
              </span>
              <span v-else class="status statusClose">
                {{ $t('home.close') }}
              </span>
              <span v-if="showTime">
                <span>,</span>
                {{
                  info.closeTime
                    ? `${$t('home.closeText')} ${info.closeTime}`
                    : '24 hours'
                }}
              </span>
            </span>
          </template>
          <q-card class="timeCard">
            <q-card-section
              v-for="(item, index) in business"
              :key="`${item}-${index}`"
            >
              <div class="row justify-between">
                <!-- <span>{{ item.date }}</span> -->
                <span>{{ $t(`home.${item.date}`) }}</span>
                <div class="text-right">
                  <div v-for="time in getTimeList(item.time)" :key="time">
                    {{ time }}
                  </div>
                </div>
              </div>
            </q-card-section>
          </q-card>
        </q-expansion-item>
      </q-card-section>
      <div v-if="place.phone" class="whatsAppbox">
        <img :src="WhatsApp" :style="{ height: '20px', width: '20px' }" />
        <a class="linkText" :href="`https://wa.me/${place.phone}`">
          {{ $t('home.whatsApp') }}
        </a>
      </div>
      <q-card-section v-if="showService" class="q-py-sm">
        <q-icon
          size="16px"
          class="beforeIcon phone text-weight-bold"
          name="font:dm-store"
        />
        <span>
          <span v-if="storeInfo.dineIn || storeInfo.fullDineIn">
            Dine-in .
          </span>
          <span v-if="storeInfo.delivery">Delivery . </span>
          <span v-if="storeInfo.pickUp">PickUp</span>
        </span>
      </q-card-section>
      <slot name="button" />
    </q-card>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  defineProps,
  watch,
  watchEffect,
  defineEmits,
} from 'vue';
import WhatsApp from 'src/assets/images/WhatsApp.png';
import { holderLogo } from 'src/utils/config';
import { useIns } from 'src/utils/v2_helper/common';
import { getDistance } from './utils';

const emit = defineEmits(['showMap']);
const props = defineProps({
  info: { type: Object, default: () => {} },
});
const app = useIns();

const openLocation = ref(false);
const distance = ref('');

const business = computed(() => props.info.businessHour || []);
const place = computed(() => props.info.storeLocation || {});
const point = computed(() => ({
  lat: place.value.latitude || 0,
  lng: place.value.longitude || 0,
}));

const showService = computed(() => {
  const { storeInfo: s } = app;
  return s.dineIn && s.fullDineIn && s.pickUp && s.delivery;
});
const showTime = computed(() => {
  const { statusCode: c } = props.info?.status || {};
  return c !== 100;
});

function _getDistance(_point) {
  openLocation.value = false;
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      openLocation.value = true;
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const point1 = { lat, lng };
      const point2 = _point;
      const _distance = getDistance(point1, point2);
      distance.value = Math.round(_distance / 1000);
    },
  );
}
function getTimeList(val) {
  return val.split(',');
}
function handleShowMap() {
  emit('showMap');
}

watchEffect(() => _getDistance(point.value));
</script>

<style lang="scss" scoped>
.infoCard {
  // 30% * 7/9
  margin-top: -23.3%;
  :deep(.cardBox) {
    font-size: 12px;
    position: relative;
    box-shadow: 0 0 0 rgba(0, 0, 0, 0.15);
    padding-bottom: 16px;
    .distance {
      color: $dark;
    }
    .linkText {
      color: $primary;
    }
    .logoImg {
      width: 120px;
      height: 120px;
      position: absolute;
      left: 50%;
      top: -60px;
      border: 4px solid #f3f4f6;
      border-radius: 50%;
      transform: translateX(-50%);
      z-index: 10;
      background: #f3f4f6;
    }
    .storeName {
      line-height: 120%;
      padding-top: 66px;
      margin-bottom: 8px;
    }
    .storeInfo {
      display: flex;
      flex-direction: column;
      justify-content: center;
      h1 {
        line-height: 28px;
      }
    }
    .beforeIcon {
      flex-shrink: 0;
      width: 32px;
      margin-right: 0px;
      color: #6b7280;
      &.time {
        font-weight: bold;
      }
      &.phone {
        box-sizing: border-box;
        padding-left: 1px;
      }
      &::before {
        justify-content: left;
      }
    }
    .expansionBox {
      padding-right: 56px;
      .status {
        &.statusOpen {
          .open {
            color: #006bd5;
          }
        }
        &.statusClose {
          color: $negative;
        }
      }
      .q-item {
        padding: 0;
        min-height: unset;
        .q-item__section--side {
          padding: 0;
          justify-content: flex-start;
          i {
            font-size: 20px;
          }
        }
      }
      .timeCard {
        padding-top: 10px;
        .q-card__section {
          padding: 5px 0px 5px 32px;
        }
      }
    }
  }
}
.whatsAppbox {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  img {
    margin-right: 12px;
  }
}
</style>
